import { useEffect, useState, useMemo } from "react";
import { Row, Col, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXDropdown, NGDropdownListItem } from "@common/components/ui-components/SXDropdown";

import { useAppSelector, useAppDispatch } from "../app/hooks";
import { selectUser } from "../features/user/userSlice";
import { useProject } from "../features/projects/projectSlice";
import { getLoadcaseX } from "@common/helpers/protoGen";
import { ng } from "@common/ngds";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";

// --- Interfaces ---
interface SXLoadComboPartProps {
  updateDominating: (lcaGuid: string, lcoGuid: string, checked: boolean) => Promise<void>;
  updateFavorable: (lcaGuid: string, lcoGuid: string, checked: boolean) => Promise<void>;
  keyIndex: number;
  part: ng.loadcombination.ICombinationPart;
  lcomb: ng.loadcombination.IData;
  userDefined?: boolean;
}

// --- Main component ---
interface SXLoadComboPartPropsWithLast extends SXLoadComboPartProps {
  isLast?: boolean;
}

export const SXLoadComboPart = (props: SXLoadComboPartPropsWithLast) => {
  // --- Contexts ---
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const { project: selProject, updateLoadCombinationPart } = useProject();

  // --- Data from Redux state ---

  const inputJson = selProject?.data.data?.input;
  const structureJson = inputJson?.structures?.[0];
  const elementJson = structureJson?.elements?.[0] || null;

  useMemo(() => {
    const input = inputJson ? ng.input.Data.fromObject(inputJson) : null;
    const structure = input?.structures?.[0] ?? null;
    const loading = structure?.loading ?? null;

    const element = structure?.elements?.[0] ?? null;
    const beam = element?.getBeam() ?? null;
  }, [inputJson]);

  // --- Local variables ---
  let project_ = inputJson ? ng.input.Data.fromObject(inputJson) : null;
  let structure_ = new ng.structure.Data();
  let struct = new ng.structure.Data();
  let loadcases_: ng.loadcase.IData[] = [];

  if (project_ && project_.structures && project_.structures[0]?.loading) {
    structure_ = project_.structures[0];
    loadcases_ = project_.structures[0].loading.loadcases || [];
  }

  const getFirstCoefficient = (part: ng.loadcombination.ICombinationPart) => {
    const coefficients = part.coefficients;
    if (!coefficients) {
      return 0;
    }
    return coefficients[0].value ?? 0;
  };

  /** Part related states */
  const [lca, setLca] = useState(props.part?.lcaseGuid ?? "");
  const [coefficientVal, setCoefficientVal] = useState(getFirstCoefficient(props.part));
  const [lcaType, setLcaType] = useState("");
  const [dominating, setDominating] = useState(false);
  const [favorable, setFavorable] = useState(props.part.favourablePermanentLoad);

  useEffect(() => {
    setLca(props.part.lcaseGuid ?? "");
    if (struct) {
      let tmpLca = getLoadcaseX(props.part.lcaseGuid ?? "", structure_);
      if (tmpLca && tmpLca.type) {
        if (
          tmpLca.type === ng.loadcase.Type.TYPE_PERMANENT_LOAD ||
          tmpLca.type === ng.loadcase.Type.TYPE_SELF_WEIGHT ||
          tmpLca.type === ng.loadcase.Type.TYPE_SOIL_SELF_WEIGHT
        ) {
          setLcaType("G");
        } else if (
          tmpLca.type === ng.loadcase.Type.TYPE_ACCIDENT_LOAD ||
          tmpLca.type === ng.loadcase.Type.TYPE_SEISMIC_LOAD ||
          tmpLca.type === ng.loadcase.Type.TYPE_SOIL_LOAD
        ) {
          setLcaType("X");
        } else {
          setLcaType("Q");
        }
      }

      if (props.lcomb.dominatingLcaseGuid === props.part.lcaseGuid) {
        setDominating(true);
      } else {
        setDominating(false);
      }
    }
    setCoefficientVal(getFirstCoefficient(props.part));
  }, [props.part]);

  //-------------------------------------------------------------------------

  // Make a dropdown list of all loadcases
  let selectableLoadCases = loadcases_
    .filter((l) => {
      // always allow self to be selectable
      if (l.id?.guid === lca) {
        return true;
      }

      // dissallow loadcases that are already used in other parts
      let used = props.lcomb.parts?.find((p) => p.lcaseGuid === l.id?.guid);
      if (used) {
        return false;
      }

      // else it must be ok
      return true;
    })
    .map((l): NGDropdownListItem<string> => {
      // todo(Gustav): how to handle a missing id???
      return { name: l.id?.name ?? "", id: l.id?.guid ?? "" };
    });

  function onPartUpdate(value: number, currentLcaID: string, nextLcaID: string) {
    if (!props.lcomb.id?.guid || !currentLcaID) {
      return;
    }

    updateLoadCombinationPart(props.lcomb.id.guid, currentLcaID, nextLcaID, value);
  }

  const updateDominatingLCA = async (lcaGuid: string, lcoGuid: string, checked: boolean) => {
    setDominating(checked);
    await props.updateDominating(lcaGuid, lcoGuid, checked);
  };

  const updateFavorableLCA = async (lcaGuid: string, lcoGuid: string, checked: boolean) => {
    setFavorable(checked);
    await props.updateFavorable(lcaGuid, lcoGuid, checked);
  };

  const inputsDisabled = props.userDefined === false;

  // --- Render ---

  return (
    <Row
      key={"part" + props.keyIndex}
      style={{
        height: 38.5,
        borderBottom: props.isLast ? "none" : "1px solid #e3dfdf",
      }}
      className="align-items-center"
    >
      <Col xs={!props.userDefined ? 4 : 6} className="text-left">
        <SXDropdown
          tableView
          id={"ltyp_" + props.keyIndex}
          name={"ltyp_" + props.keyIndex}
          dataTestId={"dropdown_ltyp_"}
          onUpdate={(value) => {
            if (value === null) {
              return;
            }
            const currentLcaID = lca;
            setLca(value.id);
            onPartUpdate(coefficientVal, currentLcaID, value.id);
          }}
          list={selectableLoadCases}
          val={selectableLoadCases.find((l) => l.id === lca)}
          lang=""
          localisation={false}
          disabled={inputsDisabled}
        ></SXDropdown>
      </Col>
      {!props.userDefined && (
        <>
          <Col xs={2} className="p-0 text-center">
            {lcaType === "Q" && (
              <>
                <Form.Check
                  type="switch"
                  className="sx-switch"
                  name={"favo_" + props.keyIndex}
                  id={"favo_" + props.keyIndex}
                  data-testid={"switch_favo"}
                  disabled={props.userDefined ? false : true}
                  checked={dominating}
                  onChange={async (e) => {
                    try {
                      if (props.part.lcaseGuid != null && e.target.checked) {
                        await updateDominatingLCA(lca, props.lcomb.id?.guid ?? "", e.target.checked);
                      }
                    } catch (x) {
                      report_exception(t, x);
                    }
                  }}
                />
              </>
            )}
            {lcaType === "G" && <>-</>}
            {lcaType === "X" && <>-</>}
          </Col>
          <Col xs={3} style={{ padding: 0, textAlign: "center" }}>
            {lcaType === "G" && (
              <>
                <Form.Check
                  type="switch"
                  className="sx-switch"
                  id="favo"
                  name={"favo_" + props.keyIndex}
                  data-testid={"switch_favo"}
                  disabled={props.userDefined ? false : true}
                  checked={favorable ?? undefined}
                  onChange={async (e) => {
                    try {
                      await updateFavorableLCA(lca, props.lcomb.id?.guid ?? "", e.target.checked);
                    } catch (x) {
                      report_exception(t, x);
                    }
                  }}
                />
              </>
            )}
            {lcaType === "Q" && <>-</>}
            {lcaType === "X" && <>-</>}
          </Col>
        </>
      )}

      <Col xs={!props.userDefined ? 3 : 6} style={{ paddingTop: 8, paddingRight: 0 }}>
        <SXEditField
          tableView
          textAlign="left"
          id={"lco_" + props.keyIndex}
          type="number"
          onUpdate={(value: number) => {
            if (lca) {
              setCoefficientVal(value);
              onPartUpdate(value, lca, lca);
            }
          }}
          val={coefficientVal}
          disableNegativeNumber={true}
          disabled={inputsDisabled}
        ></SXEditField>
      </Col>
    </Row>
  );
};
